// src/components/admin/EmbeddedCCPPanel.tsx
// CCP embebido con Amazon Connect Streams API (v2.22.x).
// Uso: no se llama initCCP al abrir; solo al hacer clic en "Load CCP panel" para evitar
// "initialize more than one time" y problemas con frame-ancestors. URL debe ser /ccp-v2.

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faChevronDown,
  faChevronUp,
  faTimes,
  faDesktop,
  faExternalLinkAlt,
  faSyncAlt,
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';

interface EmbeddedCCPPanelProps {
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
}

declare global {
  interface Window {
    connect: any;
  }
}

const EmbeddedCCPPanel = ({ isOpen, onToggle }: EmbeddedCCPPanelProps) => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const ccpContainerRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [_agentState, setAgentState] = useState<string>('Offline');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [ccpLoginUrl, setCcpLoginUrl] = useState<string>('https://wincrest.my.connect.aws/ccp-v2');
  const initTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const agentReceivedRef = useRef(false);
  const [streamsReady, setStreamsReady] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    check();
    const mql = window.matchMedia('(max-width: 768px)');
    const listener = () => setIsMobile(mql.matches);
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.connect?.core) {
      setStreamsReady(true);
      return;
    }
    const id = setInterval(() => {
      if (window.connect?.core) {
        setStreamsReady(true);
        clearInterval(id);
      }
    }, 200);
    return () => clearInterval(id);
  }, []);

  const getNormalizedCcpUrl = (rawUrl: string) => {
    try {
      const parsed = new URL(rawUrl);
      const path = parsed.pathname.replace(/\/+$/, '');

      // If only domain is provided, default to CCP v2 path.
      if (!path || path === '') {
        parsed.pathname = '/ccp-v2/';
        return parsed.toString();
      }

      // CCP v2: AWS recomienda /ccp-v2 (con trailing slash para evitar redirects).
      if (path.includes('ccp-v2')) {
        parsed.pathname = path.endsWith('/') ? path : `${path}/`;
        parsed.pathname = parsed.pathname.replace(/\/{2,}/g, '/');
        return parsed.toString();
      }

      // Fallback: append CCP v2 under current host.
      parsed.pathname = '/ccp-v2/';
      return parsed.toString();
    } catch {
      return rawUrl;
    }
  };

  // Set CCP login URL as soon as panel opens so the sign-in card can show "Open in popup" immediately.
  useEffect(() => {
    if (!isOpen || !isHome) return;
    const raw = process.env.REACT_APP_AMAZON_CONNECT_CCP_URL;
    if (raw) setCcpLoginUrl(getNormalizedCcpUrl(raw));
  }, [isOpen, isHome]);

  useEffect(() => {
    if (!isOpen || isInitialized || retryKey === 0 || !ccpContainerRef.current) {
      return;
    }

    agentReceivedRef.current = false;

    const initCCP = () => {
      try {
        const rawCcpUrl = process.env.REACT_APP_AMAZON_CONNECT_CCP_URL;
        const region =
          process.env.REACT_APP_AMAZON_CONNECT_REGION || 'us-east-1';

        if (!rawCcpUrl) {
          setAuthError(
            'CCP URL is not configured. Please check environment variables.',
          );
          console.error('REACT_APP_AMAZON_CONNECT_CCP_URL is not configured');
          return;
        }

        const ccpUrl = getNormalizedCcpUrl(rawCcpUrl);
        setCcpLoginUrl(ccpUrl);

        if (!window.connect?.core) {
          setAuthError('Amazon Connect Streams is not loaded yet. Try again in a moment.');
          return;
        }

        const container = ccpContainerRef.current;
        if (!container) return;
        container.innerHTML = '';

        window.connect.core.initCCP(container, {
          ccpUrl,
          region,
          loginPopup: false,
          loginPopupAutoClose: false,
          softphone: {
            allowFramedSoftphone: true,
            disableRingtone: true,
          },
          pageOptions: {
            enableAudioDeviceSettings: true,
            enablePhoneNumberJobAttributes: true,
          },
        });

        window.connect.agent((agent: any) => {
          agentReceivedRef.current = true;
          setIsInitialized(true);
          setAuthError(null);
          if (initTimeoutRef.current) {
            clearTimeout(initTimeoutRef.current);
            initTimeoutRef.current = null;
          }
          agent.onStateChange((state: any) => {
            setAgentState(state?.name || 'Unknown');
          });
          agent.onAfter((state: any) => {
            if (process.env.NODE_ENV === 'development') {
              console.log('CCP agent event:', state);
            }
          });
        });

        window.connect.core.onAuthFail(() => {
          setAuthError('Authentication failed. Please sign in again.');
        });

        window.connect.core.onAccessDenied(() => {
          setAuthError('Access denied. You do not have permission to use CCP.');
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        if (/more than one time|already initialized|Attempted to initialize/i.test(errorMessage)) {
          setAuthError(
            'The panel was already loaded. Please refresh the page and open the panel again to reload the CCP.'
          );
        } else {
          setAuthError(`CCP initialization error: ${errorMessage}`);
        }
        console.error('Error initializing CCP:', error);
      }
    };

    const runInit = () => {
      if (!ccpContainerRef.current) return;
      initCCP();
    };

    requestAnimationFrame(() => {
      setTimeout(runInit, 0);
    });

    initTimeoutRef.current = setTimeout(() => {
      if (agentReceivedRef.current) return;
      if (window.connect?.core?.terminate) {
        try {
          window.connect.core.terminate();
        } catch (_) {}
      }
    }, 12000);

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
    };
  }, [isOpen, isInitialized, retryKey]);

  // Al cerrar el panel: terminar CCP y resetear retryKey para que al reabrir no se intente init automático.
  useEffect(() => {
    if (!isOpen && (isInitialized || retryKey > 0) && window.connect?.core?.terminate) {
      try {
        window.connect.core.terminate();
      } catch (error) {
        console.warn('Error terminating CCP on close:', error);
      }
      setIsInitialized(false);
      setRetryKey(0);
    }
  }, [isOpen, isInitialized, retryKey]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (isInitialized && window.connect?.core?.terminate) {
        try {
          window.connect.core.terminate();
          console.log('CCP terminated');
        } catch (error) {
          console.error('Error terminating CCP:', error);
        }
      }
    };
  }, [isInitialized]);

  // No mostrar en páginas que no sean Home
  if (!isHome || !isOpen) {
    return null;
  }

  const POPUP_NAME = 'WincrestCCPSignIn';
  const POPUP_WIDTH = 420;
  const POPUP_HEIGHT = 700;

  const handleOpenPopup = () => {
    const url = ccpLoginUrl || 'https://wincrest.my.connect.aws/ccp-v2';
    const left = Math.round((window.screen.width - POPUP_WIDTH) / 2);
    const top = Math.round((window.screen.height - POPUP_HEIGHT) / 2);
    const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=yes`;
    const w = window.open(url, POPUP_NAME, features);
    if (w) w.focus();
  };

  const handleRetry = () => {
    setAuthError(null);
    if (retryKey >= 1 && window.connect?.core?.terminate) {
      try {
        window.connect.core.terminate();
      } catch (_) {}
    }
    setRetryKey((k) => k + 1);
    setIsInitialized(false);
  };

  const displaySignInPrompt = !isInitialized;
  const showSkeletonStreams = !streamsReady;
  const showSkeletonCCP = retryKey >= 1 && !isInitialized;

  const SkeletonBlock = ({ className = '' }: { className?: string }) => (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
  );

  return (
    <AnimatePresence>
      <motion.div
        key="ccp-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/20 z-[59]"
        onClick={() => onToggle(false)}
      />
      <motion.div
        key="ccp-panel"
        initial={{ opacity: 0, x: -400 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -400 }}
        transition={{ duration: 0.3 }}
        className="fixed left-0 top-[72px] md:top-[136px] h-[calc(100vh-72px)] md:h-[calc(100vh-136px)] w-[92vw] max-w-[400px] md:w-[450px] md:max-w-[450px] bg-white shadow-2xl z-[60] flex flex-col border-r border-gray-200 rounded-r-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faPhone} className="w-5 h-5" />
            <h2 className="font-semibold text-sm">Agent Contact Panel</h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              title={isCollapsed ? 'Expand' : 'Collapse'}
            >
              <FontAwesomeIcon icon={isCollapsed ? faChevronUp : faChevronDown} className="w-4 h-4" />
            </button>
            <button
              onClick={() => onToggle(false)}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* When not connected: skeleton o tarjeta. CCP container detrás para init. */}
        <div
          className={`flex-1 min-h-0 relative overflow-auto transition-all duration-300 ${
            isCollapsed ? 'hidden' : 'block'
          }`}
        >
          <div
            key={`ccp-container-${retryKey}`}
            ref={ccpContainerRef}
            className={`w-full h-full ${displaySignInPrompt ? 'invisible absolute inset-0 pointer-events-none' : 'block'}`}
            style={{ minHeight: '460px' }}
          />
          {displaySignInPrompt && (
          <div className="absolute inset-0 flex items-center justify-center p-6 bg-gradient-to-b from-slate-50 to-white overflow-auto">
            {showSkeletonStreams ? (
              <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white shadow-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <SkeletonBlock className="w-14 h-14 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <SkeletonBlock className="h-4 w-3/4" />
                    <SkeletonBlock className="h-3 w-1/2" />
                  </div>
                </div>
                <SkeletonBlock className="h-3 w-full mb-2" />
                <SkeletonBlock className="h-3 w-full mb-2" />
                <SkeletonBlock className="h-3 w-2/3 mb-4" />
                <SkeletonBlock className="h-10 w-full rounded-xl mb-3" />
                <SkeletonBlock className="h-10 w-full rounded-xl" />
                <p className="text-xs text-gray-500 mt-4 text-center">Loading Amazon Connect Streams…</p>
              </div>
            ) : showSkeletonCCP ? (
              <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white shadow-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <SkeletonBlock className="w-14 h-14 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <SkeletonBlock className="h-4 w-3/4" />
                    <SkeletonBlock className="h-3 w-1/2" />
                  </div>
                </div>
                <SkeletonBlock className="h-20 w-full rounded-lg mb-4" />
                <SkeletonBlock className="h-10 w-full rounded-xl mb-2" />
                <SkeletonBlock className="h-10 w-full rounded-xl" />
                <p className="text-xs text-blue-600 mt-4 text-center font-medium">Loading CCP panel…</p>
              </div>
            ) : (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white shadow-xl p-6 text-left"
            >
              {isMobile && (
                <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2 text-left">
                  <FontAwesomeIcon icon={faDesktop} className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">
                    This panel works best on desktop. On this device you can open the CCP in a popup using the button below.
                  </p>
                </div>
              )}
              <div className="w-14 h-14 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-4">
                <FontAwesomeIcon icon={faPhone} className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">
                Load CCP in this panel
              </h3>

              <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 mb-4 space-y-3 text-sm text-gray-800">
                <p className="font-semibold text-blue-900">Instructions</p>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>
                    Click <strong>&quot;Open CCP in popup&quot;</strong> below. A popup window will open.
                  </li>
                  <li>
                    Sign in to Amazon Connect in the popup if prompted.
                  </li>
                  <li>
                    <strong className="text-amber-800">Important:</strong> In the popup, set your status to <strong>Available</strong> (or Online) before closing. If you close the popup while still Offline, the embedded panel here will not load.
                  </li>
                  <li>
                    Close the popup once you are online/available.
                  </li>
                  <li>
                    Return here and click <strong>&quot;Load CCP panel&quot;</strong> to load the CCP inside the app.
                  </li>
                </ol>
              </div>

              {authError && (
                <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-4">
                  {authError}
                </p>
              )}
              {authError?.includes('refresh the page') && (
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="w-full mb-3 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-amber-500 text-white font-medium hover:bg-amber-600 transition-all"
                >
                  <FontAwesomeIcon icon={faSyncAlt} className="w-3.5 h-3.5" />
                  Refresh page to load panel
                </button>
              )}
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={handleRetry}
                  disabled={!streamsReady}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faSyncAlt} className="w-4 h-4" />
                  {retryKey === 0 ? 'Load CCP panel' : 'Retry loading panel'}
                </button>
                <button
                  type="button"
                  onClick={handleOpenPopup}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-medium hover:border-blue-400 hover:text-blue-700 hover:bg-blue-50/50 transition-all"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3.5 h-3.5" />
                  Open CCP in popup to sign in
                </button>
                <p className="text-xs text-gray-500 text-center">
                  <a href={ccpLoginUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-all">{ccpLoginUrl}</a>
                </p>
              </div>
            </motion.div>
            )}
          </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600 flex-shrink-0">
          <p>
            {isInitialized
              ? 'CCP is embedded in the app.'
              : isMobile
                ? 'On mobile, open the CCP in a popup with the button above.'
                : 'Open CCP in a popup, set status to Available, close the popup, then click "Load CCP panel".'}
          </p>
          {!isMobile && (
          <p className="mt-1 text-[11px] text-gray-500">
            Best experience is on desktop.
          </p>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmbeddedCCPPanel;
